import { customFetch } from "./custom-fetch";
import { useMutation } from "@tanstack/react-query";

/** Structured invoice data extracted from a PDF by the AI importer. */
export interface ParsedPdfInvoice {
  documentType: "invoice" | "quote" | "other" | null;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  dueDate: string | null;
  customer: {
    name: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
  };
  lineItems: { description: string; quantity: number; unitPrice: number }[];
  discountType: "percent" | "fixed" | null;
  discountValue: number | null;
  taxRate: number | null;
  notes: string | null;
  total: number | null;
  amountPaid: number | null;
  status: "draft" | "sent" | "paid" | "partial" | "overdue" | "void" | null;
}

export interface ParsePdfResult {
  invoice: ParsedPdfInvoice;
}

export async function parseInvoicePdf(dataBase64: string): Promise<ParsePdfResult> {
  return customFetch<ParsePdfResult>("/api/invoices/import-pdf", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ dataBase64 }),
  });
}

export function useParseInvoicePdf() {
  return useMutation({ mutationFn: parseInvoicePdf });
}

/** The reviewed/edited data the user confirms before the invoice is created. */
export interface CommitPdfInvoiceBody {
  customer: { name: string; email?: string | null; phone?: string | null; address?: string | null };
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  title?: string | null;
  notes?: string | null;
  discountType?: "percent" | "fixed" | null;
  discountValue?: number | null;
  taxRate?: number | null;
  status?: "draft" | "sent" | "paid" | "partial" | "overdue" | "void" | null;
  amountPaid?: number | null;
  lineItems: { description: string; quantity: number; unitPrice: number }[];
}

export interface CommitPdfInvoiceResult {
  invoiceId: number;
  invoiceNumber: string;
  customerId: number;
  customerCreated: boolean;
}

export async function commitInvoicePdf(body: CommitPdfInvoiceBody): Promise<CommitPdfInvoiceResult> {
  return customFetch<CommitPdfInvoiceResult>("/api/invoices/import-pdf/commit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function useCommitInvoicePdf() {
  return useMutation({ mutationFn: commitInvoicePdf });
}

/** The reviewed/edited data the user confirms before the estimate is created. */
export interface CommitPdfEstimateBody {
  customer: { name: string; email?: string | null; phone?: string | null; address?: string | null };
  estimateNumber?: string | null;
  estimateDate?: string | null;
  validUntil?: string | null;
  title?: string | null;
  notes?: string | null;
  discountType?: "percent" | "fixed" | null;
  discountValue?: number | null;
  taxRate?: number | null;
  status?: "draft" | "sent" | "accepted" | "declined" | "converted" | null;
  lineItems: { description: string; quantity: number; unitPrice: number }[];
}

export interface CommitPdfEstimateResult {
  estimateId: number;
  estimateNumber: string | null;
  customerId: number;
  customerCreated: boolean;
}

export async function commitEstimatePdf(body: CommitPdfEstimateBody): Promise<CommitPdfEstimateResult> {
  return customFetch<CommitPdfEstimateResult>("/api/estimates/import-pdf/commit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function useCommitEstimatePdf() {
  return useMutation({ mutationFn: commitEstimatePdf });
}
